// REACT
import React from 'react';
import { useTranslation } from 'react-i18next';

// SERVICES
import './CulturesMapTab.style.scss';

// Interface
import { IParcelSimu } from '../Interface/Simulation/IParcelSimu';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import { IResultInfo } from '../Interface/Simulation/IResultInfo';
import { IParcelCulture } from '../Interface/Api/Response/Exploitation/Parcels/IParcelCulture';
import { ORGANIC_STATES } from 'utils/GlobalVariables';
import dayjs from 'dayjs';
import { getParcelOrganicStateAtYear } from 'utils/tools_functions';
import { ICampaign } from '../Interface/Exploitation/Parcels/ICampaign';
import { getCultureCampaignName } from 'utils/Datagrid/CustomParcelstDataGridUtils';
import { getConstraintTypeLabel } from 'components/pages/Exploitation/Simulation/Steps/StepParcelConstraint';

type ParcelInfoParams = {
  cultureData?: any;
  parcelInfo: IParcelSimu;
  parcels?: IResultInfo[];
  forecastCampaign?: ICampaign;
  minYear?: number | null;
  from?: string | null;
  constraints?: any;
  cultures?: any[];
};

const ParcelInfo = ({
  cultureData,
  parcelInfo,
  parcels,
  forecastCampaign = { startYear: 0, endYear: 0, stringFormat: '' },
  minYear = null,
  from = null,
  constraints = null,
  cultures = [],
}: ParcelInfoParams): React.JSX.Element => {
  const { t } = useTranslation();
  const parentParcelName = parcels?.find((parcel) => parcel?.id === parcelInfo?.parent)?.name;

  const isSplittedParcel = parcelInfo?.cultures?.find((culture) => culture.isSplit);

  const parcel = parcels?.find((parcel: IResultInfo) => parcel?.id === parcelInfo?.id);

  let currentCultures = null;
  let currentIntermediateCulture = null;

  if (typeof cultureData !== 'string' && Array.isArray(cultureData)) {
    currentIntermediateCulture =
      cultureData.filter((culture) => culture.type === 'intermediate')[0]?.name || null;
    currentCultures = cultureData
      .filter((culture) => culture.type !== 'intermediate')
      .map((culture) => culture?.name)
      .join(' & ');
  }

  let isMinYear: boolean = false;
  if (forecastCampaign?.startYear > 0 && minYear !== null) {
    isMinYear = forecastCampaign.startYear <= minYear;
  }

  let currentOrganicState = 'unknown';
  if (from === 'mapParcels') {
    if (
      parcelInfo?.organicState === ORGANIC_STATES.organic ||
      parcelInfo?.organicState === ORGANIC_STATES.conventional
    ) {
      currentOrganicState = parcelInfo?.organicState;
    } else {
      if (parcelInfo?.conversionDate && forecastCampaign.startYear) {
        const currentCampaign = dayjs().year(forecastCampaign.startYear).month(7).date(1);
        const isBeforeCurrentCampain = dayjs(parcelInfo?.conversionDate).isBefore(currentCampaign);

        currentOrganicState = isBeforeCurrentCampain
          ? ORGANIC_STATES.conversion
          : ORGANIC_STATES.conventional;
      }
    }
  }
  if (from === 'mapResult' && parcel) {
    currentOrganicState = getParcelOrganicStateAtYear(parcel, forecastCampaign.startYear);
  }

  const getCultureName = (id: number) => cultures?.find((c: any) => c.id === id)?.name || id;

  return (
    <div id="popup-content">
      <div className="parcel_title">
        <h1>
          {parcelInfo?.name}{' '}
          {isSplittedParcel && (
            <div>
              <ContentCutIcon />
            </div>
          )}
        </h1>
      </div>
      <div className="popup-content__wrapper">
        <div className="popup-content__left">
          {from === 'mapResult' && !isSplittedParcel && (
            <>
              <p>
                <span className="popup-content__left__label">{t('common.culture')} :</span>
                <span className="popup-content__left__parcel">{currentCultures}</span>
              </p>
              {currentIntermediateCulture && (
                <p>
                  <span className="popup-content__left__label">{t('common.interculture')} :</span>
                  <span className="popup-content__left__parcel">{currentIntermediateCulture}</span>
                </p>
              )}
            </>
          )}
          <p>
            <span className="popup-content__left__label">
              {parcelInfo?.groupSurface ? t('label.totalSurface') : t('common.surface')} :
            </span>
            <span className="popup-content__left__parcel">
              {parcelInfo?.groupSurface
                ? Number(parcelInfo?.groupSurface.toFixed(2))
                : Number(parcelInfo?.surface.toFixed(2))}
            </span>
          </p>
          <p>
            <span className="popup-content__left__label">
              {from === 'mapResult'
                ? t('common.previous')
                : `${forecastCampaign.startYear - 1} - ${forecastCampaign.endYear - 1}`}{' '}
              :
            </span>
            <span className="popup-content__left__parcel">
              {from === 'mapResult'
                ? parcelInfo.previousCulture1.name
                : t(getCultureCampaignName(parcelInfo.parcelCultures, 1, forecastCampaign))}
            </span>
          </p>
          {!isMinYear && (
            <p>
              <span className="popup-content__left__label">
                {from === 'mapResult'
                  ? t('label.antePrevious')
                  : `${forecastCampaign.startYear - 2} - ${forecastCampaign.endYear - 2}`}{' '}
                :
              </span>
              <span className="popup-content__left__parcel">
                {from === 'mapResult'
                  ? parcelInfo.previousCulture2.name
                  : t(getCultureCampaignName(parcelInfo.parcelCultures, 2, forecastCampaign))}
              </span>
            </p>
          )}
          <p>
            <span className="popup-content__left__label">{t('label.groundType')} : </span>
            <span className="popup-content__left__parcel">{parcelInfo?.groundType?.name}</span>
          </p>
          <p>
            <span className="popup-content__left__label">{t('common.irrigability')} :</span>
            <span className="popup-content__left__parcel">
              {parcelInfo?.isIrrigableOnly || parcelInfo?.isIrrigable
                ? t('common.yes')
                : t('common.no')}
            </span>
          </p>
          {(from === 'mapResult' || from === 'mapParcels') && (
            <p>
              <span className="popup-content__left__label">{t('common.parcel')} :</span>
              <span className="popup-content__left__parcel">
                {currentOrganicState === ORGANIC_STATES.conversion
                  ? t('organicState.conversion')
                  : currentOrganicState === ORGANIC_STATES.organic
                    ? t('organicState.organic')
                    : t('organicState.conventional')}
              </span>
            </p>
          )}
        </div>
        {isSplittedParcel && (
          <div className="popup-content__right">
            {parcelInfo?.cultures.map((culture, index) => (
              <div key={index}>
                <h4 className="popup-content__right__title">
                  {t('common.parcel')} {index + 1}
                </h4>
                <p>
                  <span className="popup-content__right__label">{t('common.culture')} :</span>
                  <span className="popup-content__right__culture">{culture?.name}</span>
                </p>
                <p>
                  <span className="popup-content__right__label">{t('common.surface')} :</span>
                  <span className="popup-content__right__culture">
                    {Number(culture?.surface.toFixed(2))}
                  </span>
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
      {parcelInfo?.parent && (
        <p>
          {parentParcelName ? (
            <span>
              <i>{t('message.parcelGroupedWith', { name: parentParcelName })}</i>
            </span>
          ) : (
            <span>
              <i>{t('message.parcelPartOfGrouping')}</i>
            </span>
          )}
        </p>
      )}
      {parcelInfo?.isParent && (
        <p>
          <span>
            <i>{t('message.parcelParentOfGrouping')}</i>
          </span>
        </p>
      )}
      {constraints && (
        <div>
          {constraints
            ?.filter((constraint: any) => constraint.parcels.includes(parcelInfo.id))
            .map((constraint: any) => (
              <div key={constraint.id}>
                <div>
                  <span
                    className={`constraint-card__type constraint-card__type--${constraint.type}`}
                  >
                    {getConstraintTypeLabel(constraint.constraint)}
                  </span>
                </div>
                <div>
                  <div className="constraint-card__section">
                    <strong>{t('common.cultures')}:</strong>
                    <span>
                      {constraint?.cultures.map((id: number) => getCultureName(id)).join(', ')}
                    </span>
                  </div>
                  <div className="constraint-card__section">
                    <strong>{t('common.campaigns')}:</strong>
                    <span>{constraint?.years.join(', ')}</span>
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default ParcelInfo;
